(() => {
  const cardSelector = '.product-card-wrapper';
  const panelSelector = '.card__hover-panel';
  const measurementSelector = '[data-card-reveal-measurement]';
  const mediaQuery = window.matchMedia('(hover: hover) and (min-width: 990px)');
  const initialized = new WeakSet();
  const cards = new Set();
  const hoveredCards = new WeakSet();
  const openCards = new Set();
  const closeEndHandlers = new WeakMap();
  const closeFallbacks = new WeakMap();
  let dynamicCardsObserver;
  let refreshQueued = false;
  let refreshAfterClose = false;

  const getRevealHost = card =>
    card.closest('.grid__item, .fp-rail__slide, .arctic-rail__slide, .swiper-slide') || card.parentElement;

  const getRevealViewport = card => card.closest('.arctic-rail__viewport, .slider');

  const isMeasurementElement = element =>
    element instanceof Element &&
    (element.matches(measurementSelector) || Boolean(element.closest(measurementSelector)));

  const syncViewportOpenState = viewport => {
    if (!viewport) return;

    const hasOpenCard = Array.from(openCards).some(card => getRevealViewport(card) === viewport);
    viewport.classList.toggle('product-card-reveal-viewport--open', hasOpenCard);
    viewport.toggleAttribute('data-card-reveal-viewport-open', hasOpenCard);
  };

  const hasRevealContent = panel => {
    if (!panel) return false;

    return Boolean(
      panel.querySelector('button, a, input, select, textarea, [data-northbound-price-surface]') ||
        panel.textContent.trim()
    );
  };

  const shouldRemainOpen = card => hoveredCards.has(card) || card.contains(document.activeElement);

  const cancelClose = card => {
    const closeEndHandler = closeEndHandlers.get(card);
    const closeFallback = closeFallbacks.get(card);

    if (closeEndHandler) {
      card.removeEventListener('transitionend', closeEndHandler);
      closeEndHandlers.delete(card);
    }

    if (closeFallback) {
      window.clearTimeout(closeFallback);
      closeFallbacks.delete(card);
    }

    card.removeAttribute('data-card-reveal-closing');
  };

  const clearCard = card => {
    const revealHost = getRevealHost(card);
    const revealViewport = getRevealViewport(card);

    cancelClose(card);
    hoveredCards.delete(card);
    openCards.delete(card);
    card.removeAttribute('data-card-reveal-ready');
    card.removeAttribute('data-card-reveal-open');
    card.removeAttribute('data-card-reveal-closing');
    card.style.removeProperty('--card-reveal-base-height');
    card.style.removeProperty('--card-reveal-open-height');
    card.style.removeProperty('--card-reveal-media-height');

    if (revealHost) {
      revealHost.removeAttribute('data-card-reveal-host');
      revealHost.removeAttribute('data-card-reveal-host-open');
      revealHost.classList.remove('product-card-reveal-host', 'product-card-reveal-host--open');
      revealHost.style.removeProperty('--card-reveal-base-height');
    }

    syncViewportOpenState(revealViewport);
  };

  const prepareClone = (card, width) => {
    const clone = card.cloneNode(true);
    const cloneCard = clone.querySelector('.card');
    const cloneMediaFrame = clone.querySelector('.card__inner');
    const clonePanel = clone.querySelector(panelSelector);

    clone.removeAttribute('data-card-reveal-ready');
    clone.removeAttribute('data-card-reveal-open');
    clone.removeAttribute('data-card-reveal-closing');
    clone.setAttribute('data-card-reveal-measurement', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.classList.remove('product-card-reveal-host', 'product-card-reveal-host--open');
    clone.style.removeProperty('--card-reveal-base-height');
    clone.style.removeProperty('--card-reveal-open-height');
    clone.style.removeProperty('--card-reveal-media-height');

    Object.assign(clone.style, {
      position: 'fixed',
      inset: '0 auto auto -10000px',
      zIndex: '-1',
      width: `${width}px`,
      height: 'auto',
      minHeight: '0',
      visibility: 'hidden',
      pointerEvents: 'none',
      transition: 'none',
    });

    if (cloneCard) {
      cloneCard.style.height = 'auto';
      cloneCard.style.minHeight = '0';
    }

    if (cloneMediaFrame) {
      cloneMediaFrame.style.flex = '0 0 auto';
      cloneMediaFrame.style.height = 'auto';
    }

    clone.querySelectorAll('.card > .card__content').forEach(content => {
      content.style.flexGrow = '0';
      content.style.height = 'auto';
      content.style.minHeight = '0';
    });

    if (clonePanel) {
      clonePanel.style.visibility = 'hidden';
      clonePanel.style.opacity = '0';
      clonePanel.style.pointerEvents = 'none';
      clonePanel.style.transform = 'translateY(100%)';
    }

    return { clone, cloneMediaFrame, clonePanel };
  };

  const measureClosedCard = card => {
    const width = card.getBoundingClientRect().width;
    const { clone, cloneMediaFrame, clonePanel } = prepareClone(card, width);

    document.body.appendChild(clone);

    const measurement = {
      baseHeight: clone.getBoundingClientRect().height,
      mediaHeight: cloneMediaFrame ? cloneMediaFrame.getBoundingClientRect().height : 0,
      panelHeight: clonePanel ? clonePanel.getBoundingClientRect().height : 0,
    };

    clone.remove();

    return measurement;
  };

  const collectMeasurableCards = () => {
    const measurableCards = [];

    cards.forEach(card => {
      const panel = card.querySelector(panelSelector);
      const mediaFrame = card.querySelector('.card__inner');
      const revealHost = getRevealHost(card);

      if (!hasRevealContent(panel) || !mediaFrame || !revealHost || !mediaQuery.matches) {
        clearCard(card);
        return;
      }

      measurableCards.push({ card, revealHost });
    });

    return measurableCards;
  };

  const applyMeasurements = measuredCards => {
    const rows = [];

    measuredCards.forEach(measuredCard => {
      const hostTop = measuredCard.revealHost.getBoundingClientRect().top;
      let row = rows.find(candidate => Math.abs(candidate.top - hostTop) < 2);

      if (!row) {
        row = { top: hostTop, cards: [], baseHeight: 0 };
        rows.push(row);
      }

      row.cards.push(measuredCard);
      row.baseHeight = Math.max(row.baseHeight, measuredCard.measurement.baseHeight);
    });

    rows.forEach(row => {
      row.cards.forEach(({ card, revealHost, measurement }) => {
        const baseHeight = row.baseHeight;
        const openHeight = baseHeight + measurement.panelHeight;

        card.style.setProperty('--card-reveal-base-height', `${baseHeight}px`);
        card.style.setProperty('--card-reveal-open-height', `${openHeight}px`);
        card.style.setProperty('--card-reveal-media-height', `${measurement.mediaHeight}px`);
        revealHost.style.setProperty('--card-reveal-base-height', `${baseHeight}px`);
        revealHost.classList.add('product-card-reveal-host');
        revealHost.setAttribute('data-card-reveal-host', '');
        card.setAttribute('data-card-reveal-ready', '');

        if (shouldRemainOpen(card)) openCard(card);
      });
    });
  };

  const refreshCards = () => {
    refreshQueued = false;

    if (openCards.size > 0) {
      refreshAfterClose = true;
      return;
    }

    const measurableCards = collectMeasurableCards();

    measurableCards.forEach(({ card }) => clearCard(card));

    const measuredCards = measurableCards.map(({ card, revealHost }) => ({
      card,
      revealHost,
      measurement: measureClosedCard(card),
    }));

    applyMeasurements(measuredCards);
  };

  const scheduleRefresh = () => {
    if (openCards.size > 0) {
      refreshAfterClose = true;
      return;
    }

    if (refreshQueued) return;

    refreshQueued = true;
    requestAnimationFrame(refreshCards);
  };

  const openCard = card => {
    if (!mediaQuery.matches || !card.hasAttribute('data-card-reveal-ready')) return;

    cancelClose(card);

    const revealHost = getRevealHost(card);
    if (revealHost) {
      revealHost.classList.add('product-card-reveal-host--open');
      revealHost.setAttribute('data-card-reveal-host-open', '');
    }

    openCards.add(card);
    card.setAttribute('data-card-reveal-open', '');
    syncViewportOpenState(getRevealViewport(card));
  };

  const closeCard = card => {
    if (!card.hasAttribute('data-card-reveal-open') || card.hasAttribute('data-card-reveal-closing')) return;

    const revealViewport = getRevealViewport(card);
    const revealHost = getRevealHost(card);
    card.removeAttribute('data-card-reveal-open');
    card.setAttribute('data-card-reveal-closing', '');

    const finishClose = () => {
      cancelClose(card);

      if (revealHost) {
        revealHost.classList.remove('product-card-reveal-host--open');
        revealHost.removeAttribute('data-card-reveal-host-open');
      }

      openCards.delete(card);
      syncViewportOpenState(revealViewport);

      if (openCards.size === 0 && refreshAfterClose) {
        refreshAfterClose = false;
        scheduleRefresh();
      }
    };

    const closeEndHandler = event => {
      if (event.target === card && event.propertyName === 'height') finishClose();
    };

    closeEndHandlers.set(card, closeEndHandler);
    closeFallbacks.set(card, window.setTimeout(finishClose, 350));
    card.addEventListener('transitionend', closeEndHandler);
  };

  const requestCloseCard = card => {
    requestAnimationFrame(() => {
      if (!shouldRemainOpen(card)) closeCard(card);
    });
  };

  const observeCard = card => {
    if ('ResizeObserver' in window) {
      const panel = card.querySelector(panelSelector);
      const mediaFrame = card.querySelector('.card__inner');
      const content = card.querySelector('.card > .card__content');
      const resizeObserver = new ResizeObserver(scheduleRefresh);

      [panel, mediaFrame, content].filter(Boolean).forEach(element => resizeObserver.observe(element));
    }

    card.querySelectorAll('img').forEach(image => {
      if (!image.complete) image.addEventListener('load', scheduleRefresh, { once: true });
    });
  };

  const initializeCard = card => {
    if (!card.isConnected || isMeasurementElement(card)) return;

    const panel = card.querySelector(panelSelector);

    if (!hasRevealContent(panel) || initialized.has(card)) return;

    initialized.add(card);
    cards.add(card);

    const hoverTarget = getRevealHost(card) || card;

    hoverTarget.addEventListener('mouseenter', () => {
      hoveredCards.add(card);
      openCard(card);
    });
    hoverTarget.addEventListener('mouseleave', () => {
      hoveredCards.delete(card);
      requestCloseCard(card);
    });
    card.addEventListener('focusin', () => openCard(card));
    card.addEventListener('focusout', () => requestCloseCard(card));

    observeCard(card);
    scheduleRefresh();
  };

  const initializeCards = root => {
    if (root instanceof Element && (!root.isConnected || isMeasurementElement(root))) return;

    if (root.matches && root.matches(cardSelector)) initializeCard(root);
    root.querySelectorAll(cardSelector).forEach(card => {
      if (!isMeasurementElement(card)) initializeCard(card);
    });
  };

  const observeDynamicCards = () => {
    if (!('MutationObserver' in window) || dynamicCardsObserver || !document.body) return;

    dynamicCardsObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) initializeCards(node);
        });
      });
    });

    dynamicCardsObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  };

  const initializeAllCards = () => {
    initializeCards(document);
    observeDynamicCards();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAllCards);
  } else {
    initializeAllCards();
  }

  document.addEventListener('shopify:section:load', event => initializeCards(event.target));
  window.addEventListener('resize', scheduleRefresh);
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', scheduleRefresh);
  } else {
    mediaQuery.addListener(scheduleRefresh);
  }
})();
